import Button from '@cohort/merchants/components/buttons/Button';
import {InputWithLabel} from '@cohort/merchants/components/form/InputWithLabel';
import {Dialog, DialogContent} from '@cohort/merchants/components/modals/Dialog';
import {isEmailValid} from '@cohort/shared/utils/validation';
import {PaperPlaneRight} from '@phosphor-icons/react';
import React, {useCallback, useState} from 'react';
import {useTranslation} from 'react-i18next';

interface InviteMemberModalProps {
  addMembership: CallableFunction;
  show: boolean;
  handleClose: () => void;
}

const InviteMemberModal = ({
  addMembership,
  show,
  handleClose,
}: InviteMemberModalProps): JSX.Element => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const isValidEmail = isEmailValid(email);

  const {t} = useTranslation('pages', {keyPrefix: 'settings.members.inviteMemberModal'});

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setLoading(true);
      await addMembership(email);
      setLoading(false);
      setEmail('');
      handleClose();
    },
    [addMembership, email, handleClose]
  );

  const handleEmailChanged = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setEmail(event.target.value);
    },
    [setEmail]
  );

  return (
    <Dialog open={show} onOpenChange={handleClose}>
      <DialogContent>
        <form onSubmit={handleSubmit} className="space-y-4 p-6">
          <div className="text-center text-lg font-semibold">{t('labelInviteUser')}</div>
          <InputWithLabel
            type="text"
            disabled={loading}
            placeholder="Enter the email"
            onChange={handleEmailChanged}
            testId="email"
            subtitle={t('labelSendEmail')}
          />
          <div>
            <Button
              type="submit"
              disabled={!isValidEmail || loading}
              loading={loading}
              className="h-12 w-full"
              data-testid="send-invitation"
            >
              {t('buttonSendInvitation')}
              <PaperPlaneRight className="ml-3 h-5 w-5" />
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default InviteMemberModal;
